<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useRouter } from 'vue-router/composables'

  import GetStartedPanelLoader from '@/components/loaders/GetStartedPanelLoader.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import ContactMe from '@/components/common/ContactMe.vue'

  import useIcons from '@/composables/useIcons'
  import useI18n from '@/composables/useI18n'
  import useVideo from '@/composables/useVideo'
  import useCourse from '@/composables/useCourse'

  import { UserModule } from '@/store/modules/user'
  import useCommonMixin from '@/composables/useCommonMixin'

  interface Step {
    active: boolean
    completed: boolean
    requiresUser?: boolean
    action: () => void
    getTitle: () => string
  }

  const router = useRouter()

  const { checkboxSquare, checkedCircle, michaelPortrait, play, expandIcon, collapseIcon } = useIcons()
  const { translateString } = useI18n()
  const { getPosterImage } = useVideo()
  const { hasValidUser, showCompanyOnboardingPopupDetails } = useCommonMixin()
  const { course, nextModuleToStart, navigateToCourseContent, navigateToModule, showStartCourseForFreeModal } =
    useCourse()

  const isCollapsed = ref(false)

  const loading = computed(() => UserModule.fetchingOnboardingPopupDetails || !course.value)

  const companyOnboardingPopupDetails = computed(() => UserModule.companyOnboardingPopupDetails)

  const introVideoWatched = computed(() => UserModule.getStartedChecklist?.introVideoWatched ?? false)

  const steps = computed<Step[]>(() => {
    const baseSteps: Step[] = [
      {
        active: false,
        completed: UserModule.getStartedChecklist?.accountCreated ?? false,
        requiresUser: false,
        action: () => router.push('/login'),
        getTitle: () => translateString('homepage.getStartedChecklist.createYourAccount'),
      },
    ]

    if (companyOnboardingPopupDetails.value) {
      baseSteps.push({
        active: false,
        completed: introVideoWatched.value,
        requiresUser: true,
        action: () => showCompanyOnboardingPopupDetails(),
        getTitle: () => translateString('homepage.getStartedChecklist.introVideoWatched'),
      })
    }

    baseSteps.push(
      {
        active: false,
        completed:
          (UserModule.getStartedChecklist?.read5Lessons ?? false) || (course.value?.numberOfLessonsCompleted ?? 0) >= 5,
        requiresUser: true,
        action: () => handleRouteToNextLesson(),
        getTitle: () => {
          const lessonsCompleted = course.value?.numberOfLessonsCompleted ?? 0
          if (lessonsCompleted >= 5) {
            return translateString('homepage.getStartedChecklist.read5Lessons')
          }
          if (lessonsCompleted === 4) {
            return translateString('homepage.getStartedChecklist.readOneMoreLesson')
          }
          if (lessonsCompleted === 3) {
            return translateString('homepage.getStartedChecklist.read2MoreLessons')
          }
          if (lessonsCompleted === 2) {
            return translateString('homepage.getStartedChecklist.read3MoreLessons')
          }
          if (lessonsCompleted === 1) {
            return translateString('homepage.getStartedChecklist.read4MoreLessons')
          }
          return translateString('homepage.getStartedChecklist.read5Lessons')
        },
      },
      {
        active: false,
        completed: UserModule.getStartedChecklist?.bookmarkLesson ?? false,
        requiresUser: true,
        action: () => handleRouteToNextLesson(true),
        getTitle: () => translateString('homepage.getStartedChecklist.bookmarkALesson'),
      },
    )

    if (course.value?.exam) {
      baseSteps.push({
        active: false,
        completed: UserModule.getStartedChecklist?.startExamSimulation ?? false,
        requiresUser: true,
        action: () => router.push(`/courses/${course.value!.id}/exam/${course.value!.exam!.id}/start`),
        getTitle: () => translateString('homepage.getStartedChecklist.startAnExam'),
      })
    }

    return baseSteps
  })

  const allStepsCompleted = computed(() => steps.value.every((step) => step.completed))

  const nextIncompleteStepIndex = computed(() => steps.value.findIndex((step) => !step.completed))

  const popupPosterImage = computed(() => {
    if (!companyOnboardingPopupDetails.value) return ''

    return (
      companyOnboardingPopupDetails.value.posterImageUrl ??
      getPosterImage(
        companyOnboardingPopupDetails.value.videoUrl ?? '',
        companyOnboardingPopupDetails.value.timestamp ?? 0,
      )
    )
  })

  const nextLesson = computed(() => {
    if (!nextModuleToStart.value) return null

    return nextModuleToStart.value.lessons.find((lesson) => !lesson.completed) || nextModuleToStart.value.lessons[0]
  })

  const updateActiveStep = () => {
    steps.value.forEach((step, index) => {
      step.active = index === nextIncompleteStepIndex.value
    })
  }

  const handleStepClick = (step: Step) => {
    if (step.requiresUser && !hasValidUser.value) {
      router.push('/login')
      return
    }

    if (step.completed) return

    if (step.action) {
      step.action()
    }
  }

  const handleRouteToNextLesson = (shouldScrollToBookmark?: boolean) => {
    if (course.value?.purchased || course.value?.expired) {
      navigateToCourseContent(nextModuleToStart.value, nextLesson.value, course.value!.id, false)
    } else if (course.value?.trial) {
      navigateToModule(course.value!.id, nextModuleToStart.value, nextLesson.value, shouldScrollToBookmark)
    } else {
      showStartCourseForFreeModal({
        scrollToBookmark: shouldScrollToBookmark,
      })
    }
  }

  const toggleCollapse = (event: MouseEvent) => {
    if (isCollapsed.value) {
      isCollapsed.value = false
    } else {
      event.stopPropagation()
      isCollapsed.value = true
    }
  }

  watch(nextIncompleteStepIndex, updateActiveStep, { immediate: true })
  watch(steps, updateActiveStep, { immediate: true })
  watch(
    allStepsCompleted,
    (completed) => {
      if (completed) {
        isCollapsed.value = true
      }
    },
    { immediate: true },
  )
</script>

<template lang="pug">
.get-started-panel-wrapper
  GetStartedPanelLoader(v-if='loading')
  .get-started-panel(
    v-else,
    :class='{ "is-collapsed": isCollapsed, "showing-help-content": introVideoWatched || !companyOnboardingPopupDetails }'
  )
    .close-panel-cta(@click.stop='toggleCollapse', v-if='!isCollapsed && UserModule.hasValidUser')
      SVGRenderer(
        :icon='collapseIcon',
        width='20px',
        height='20px',
        :fill-color='"var(--primary-text-color)"',
        :has-hover='false'
      )
    .collapsed-content-wrapper(v-if='isCollapsed', @click='toggleCollapse')
      .collapsed-content
        .image-portrait
          img(:src='michaelPortrait')
        .text-portrait
          h5.title {{ $t('helpDetails.stillQuestions') }}
          h5.description {{ $t('helpDetails.feelFreeToContactMe') }}
      SVGRenderer(
        :icon='expandIcon',
        width='20px',
        height='20px',
        :fill-color='"var(--primary-text-color)"',
        :has-hover='false'
      )
    template(v-else)
      .left-side
        h2.title {{ $t('homepage.getStartedChecklist.yourFirstSteps') }}
        .steps
          .step(
            v-for='(step, index) in steps',
            :key='index',
            @click='handleStepClick(step)',
            :class='{ "has-cta": !step.completed, completed: step.completed }'
          )
            .status-icon
              SVGRenderer(
                :icon='checkedCircle',
                width='15px',
                v-if='step.completed',
                :stroke-color='"var(--primary-color)"',
                :has-hover='false'
              )
              SVGRenderer(
                :icon='checkboxSquare',
                width='15px',
                v-else,
                :stroke-color='"var(--primary-text-color)"',
                :has-hover='false'
              )
            h4.step-title(:class='{ active: step.active, "has-cta": !step.completed }') {{ step.getTitle() }}
      .right-side(:class='{ "showing-help-content": introVideoWatched || !companyOnboardingPopupDetails }')
        .intro-teaser-image(
          v-if='!introVideoWatched && companyOnboardingPopupDetails',
          @click='showCompanyOnboardingPopupDetails'
        )
          img(:src='popupPosterImage')
          .play-overlay(v-if='popupPosterImage')
            SVGRenderer(
              :icon='play',
              width='25px',
              height='25px',
              :stroke-color='"var(--primary-foreground-color)"',
              :has-hover='false'
            )
        ContactMe(v-else)
</template>

<style lang="postcss">
  .get-started-panel-wrapper {
    .get-started-panel {
      @apply ketch-bg-content-background-color ketch-p-c10 ketch-rounded-normal ketch-flex ketch-flex-col-reverse ketch-shadow-content-shadow ketch-relative;
      @apply xs1:ketch-p-c15 sm:ketch-rounded-large sm:ketch-p-c20 sm:ketch-flex-row sm:ketch-space-y-0 sm:ketch-grid sm:ketch-grid-cols-2 sm:ketch-gap-c20;

      &.is-collapsed {
        @apply ketch-cursor-pointer ketch-p-c10 xs1:ketch-p-c15 sm:ketch-p-c20 sm:ketch-grid-cols-none;

        .collapsed-content-wrapper {
          @apply ketch-flex ketch-items-center ketch-justify-between;

          .collapsed-content {
            @apply ketch-flex ketch-items-center ketch-space-x-c10;

            .image-portrait {
              @apply ketch-w-c50;
            }

            .text-portrait {
              @apply ketch-flex ketch-flex-col;

              .title {
                @apply ketch-font-bold;
              }
            }
          }
        }
      }

      &.showing-help-content {
        @apply ketch-flex-col sm:ketch-pr-0;

        &.is-collapsed {
          @apply ketch-pr-c10 sm:ketch-pr-c20;
        }

        .left-side {
          @apply ketch-mt-0 ketch-mb-c20 sm:ketch-mb-0;
        }
      }

      .close-panel-cta {
        @apply ketch-h-c30 ketch-w-c30 ketch-flex ketch-items-center ketch-justify-center ketch-bg-content-background-color ketch-rounded-full;
        @apply ketch-absolute ketch-top-[-5px] ketch-right-[-5px] ketch-cursor-pointer ketch-shadow-content-shadow ketch-z-[2];
        @apply sm:ketch-top-[-10px] sm:ketch-right-[-10px];
      }

      .left-side {
        @apply ketch-flex ketch-flex-col ketch-space-y-c10 ketch-mt-c20 md:ketch-mt-0;

        .title {
          @apply ketch-font-bold;
        }

        .steps {
          @apply ketch-flex ketch-flex-col ketch-space-y-c10 md:ketch-pl-c20;

          .step {
            @apply ketch-flex ketch-items-center ketch-space-x-c10;

            &.has-cta {
              @apply ketch-cursor-pointer hover:ketch-underline;
            }

            &.completed {
              @apply ketch-opacity-40;
            }

            .status-icon {
              @apply ketch-flex ketch-items-center ketch-justify-center ketch-w-c15 ketch-h-c15;
            }

            .step-title {
              &.active {
                @apply ketch-font-bold;
              }

              &.has-cta {
                @apply ketch-cursor-pointer hover:ketch-underline;
              }
            }
          }
        }
      }

      .right-side {
        @apply ketch-w-full;

        &.showing-help-content {
          @apply sm:ketch--my-c20 sm:ketch-bg-tertiary-color sm:ketch-rounded-r-large;
        }

        .intro-teaser-image {
          @apply ketch-h-full ketch-cursor-pointer ketch-relative;

          img {
            @apply ketch-w-full ketch-h-full ketch-object-cover ketch-rounded-normal;
          }
        }
      }
    }
  }
</style>
